import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Plus } from 'lucide-react';
import Breadcrumb from '../components/breadcrumb';
import StepProgressBar from '../components/StepProgressBar';
import JobsiteDetailsStep1 from '../components/JobsiteDetailsStep1';
import AddressStep, { POSTAL_CODE_PROVINCE_MAP } from '../components/AddressStep';
import Dialog from '../components/Dialog';
import { JobsiteService } from '../services/jobsite.service';
import { useEditForm } from '../hooks/useEditForm';
import { useJobsiteForm } from '../hooks/useJobsiteForm';
import { authService } from '../services/auth.service';
import { lookupService } from '../services/lookups.service';
import { GeneralContractorService } from '../services/general-contractor.service';
import { Trade } from '../components/contractor-management/types';

// Navigation Component
const Navigation = ({ onBack }: { onBack: () => void }) => (
  <div className="mb-6 flex items-center">
    <ArrowLeft
      className="text-gray-600 mr-2 w-5 h-5 cursor-pointer"
      onClick={onBack}
      id="back-button"
    />
    <Breadcrumb title="Add New Jobsite" />
  </div>
);

const AddJobsite: React.FC = () => {

  // Get current user
  const user = authService.getUser();

  const navigate = useNavigate();

  // Get data from useEditForm hook (same as other components)
  const {
    completionSteps,
    sectors,
    industries,
    fundingResources,
    provinces
  } = useEditForm();

  // Fetch general contractors separately (not from hook)
  const [generalContractors, setGeneralContractors] = useState<any[]>([]);

  // Use jobsite form hook for shared logic
  const {
    isProcessing,
    setIsProcessing,
    processingText,
    setProcessingText,
    handleCreateJobsiteAddress
  } = useJobsiteForm();

  // State for form steps
  const [currentStep, setCurrentStep] = useState(1);
  const [hasEdits, setHasEdits] = useState(false);

  // Dialog states
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdJobsiteId, setCreatedJobsiteId] = useState<string | null>(null);


  // Form data state (matching JobsiteAddDialog structure)
  const initialFormState = {
    name: '',
    subsite: '',
    contact: '',
    projectType: '',
    percentageCompleted: '',
    isUnion: false,
  generalContractor: '',
    address: '',
    city: '',
    country: '',
    state: '',
    postalCode: '',
    superintendent: '',
    siteContactEmail: '',
    sectors: [] as string[],
    industry: '',
    fundingResource: '',
    activityStatus: 'active',
    completionStatus: 'Pre-construction',
    completionStep: 'Pre-construction',
    address1: '',
    address2: '',
    markAsComp: false,
    status: 'active' as 'active' | 'inactive',
    hasPLA: false,
    hasMRF: false,
    projectStartDate: ''
  };

  const [formData, setFormData] = useState(initialFormState);


  // Trade options
  const [tradeOptions, setTradeOptions] = useState<Trade[]>([]);

  // Error states
  const [errors, setErrors] = useState<Record<string, string>>({});



  // Step configuration
  const FORM_STEPS = [
    { id: 1, title: 'Job Details', description: 'Basic jobsite information and contractor details' },
    { id: 2, title: 'Address', description: 'Jobsite location and address information' }
  ];

  // Initialize completion step when job completion statuses are available
  useEffect(() => {
    if (completionSteps.length > 0 && formData.completionStep === 'Pre-construction') {
      setFormData(prev => ({
        ...prev,
        completionStep: completionSteps[0].value,
        completionStatus: completionSteps[0].label
      }));
    }
  }, [completionSteps, formData.completionStep]);


  // Fetch general contractors
  useEffect(() => {
    const fetchGeneralContractors = async () => {
      try {
        const contractors = await GeneralContractorService.getGeneralContractors();
        setGeneralContractors(contractors);
      } catch (error) {
        toast.error('Error fetching general contractors');
      }
    };
    fetchGeneralContractors();
  }, []);

  // Fetch trades data
  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const trades = await lookupService.getTrades();
        // Filter to only show UA trades
        const uaTrades = trades.filter(t => t.category === 'UA');
        setTradeOptions(uaTrades);
      } catch (error) {
        toast.error('Error fetching trades');
      }
    };
    fetchTrades();
  }, []);

  // Navigation handlers
  const handleBack = () => {
    if (hasEdits) {
      toast.warn('You have made edits, and it has not been saved');
    }
    navigate('/jobsite-management');
  };

  // Input change handlers (matching JobsiteAddDialog pattern)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => {
      const updatedForm = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      const isEdited = Object.keys(initialFormState).some(key => {
        const initial = initialFormState[key as keyof typeof initialFormState];
        const current = updatedForm[key as keyof typeof updatedForm];

        if (initial === '' || initial === false) {
          return current !== initial && (current !== '' && current !== false);
        }
        return current !== initial;
      });

      setHasEdits(isEdited);

      return updatedForm;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };



  // Validation functions (following JobsiteAddDialog pattern)
  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Jobsite name is required';
    }
    if (!formData.sectors || formData.sectors.length === 0) {
      newErrors.sectors = 'At least one sector is required';
    }

    // Check if there are industries available for the selected sectors
    // Only require industry if exactly ONE sector is selected and it has industries
    const availableIndustries = formData.sectors && formData.sectors.length === 1
      ? industries.filter(industry => formData.sectors.includes(industry.sector.id.toString()))
      : [];

    // Only require industry if there are industries available for the single selected sector
    if (availableIndustries.length >= 2 && !formData.industry) {
      newErrors.industry = 'Industry is required';
    }

    if (!formData.projectStartDate) {
      newErrors.projectStartDate = 'Project start date is required';
    }
    if (!formData.completionStep) {
      newErrors.completionStep = 'Completion status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.address1.trim()) {
      newErrors.address1 = 'Address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state) {
      newErrors.state = 'Province is required';
    }
    // if (!formData.postalCode.trim()) {
    //   newErrors.postalCode = 'Postal code is required';
    // }

    // Postal code format validation (Canadian format)
    const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    if (formData.postalCode && !postalCodeRegex.test(formData.postalCode)) {
      newErrors.postalCode = 'Please enter a valid Canadian postal code (e.g., A1A 1A1)';
    } else if (formData.postalCode && formData.state) {
      const prefix = formData.postalCode[0].toUpperCase();
      const validProvinces = POSTAL_CODE_PROVINCE_MAP[prefix];
      const selectedProvince = provinces.find((p: { id: string | number; name: string }) => p.id.toString() === formData.state);
      if (validProvinces && selectedProvince && !validProvinces.includes(selectedProvince.name)) {
        newErrors.postalCode = `This postal code does not belong to ${selectedProvince.name}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!user || !user.id) return;
    try {
      setIsProcessing(true);
      // Prepare jobsite data for step 1
      const jobsiteData = {
        jobSiteName: formData.name,
        subsite: formData.subsite,
        siteContact: formData.superintendent,
        generalContractor: formData.generalContractor,
        siteContactEmail: formData.siteContactEmail,
        sectors: formData.sectors,
        industry: formData.industry,
        fundingResource: formData.fundingResource || undefined,
        markAsComp: formData.markAsComp,
        status: formData.status,
        jobCompletionStatus: formData.completionStep,
        userId: user.id,
        hasPLA: formData.hasPLA,
        hasMRF: formData.hasMRF,
        projectStartDate: formData.projectStartDate
      };

      // Prepare address data draft for duplicate check
      const addressDataDraft = {
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        province: formData.state,
        postalCode: formData.postalCode || '',
      };

      // Check for duplicate BEFORE creating jobsite
      setProcessingText('Checking for duplicates...');
      const duplicateCheck = await JobsiteService.checkDuplicateJobsite({
        jobSiteName: jobsiteData.jobSiteName,
        generalContractor: jobsiteData.generalContractor,
        address: addressDataDraft
      });

      if (duplicateCheck.isDuplicate) {
        throw new Error('A jobsite with this name, address, and general contractor combination already exists');
      }

      const createdJobsite = await JobsiteService.createJobsite(jobsiteData);

      if (!createdJobsite?.id) {
        throw new Error('Failed to create jobsite');
      }

      // Use hook to handle address creation with geocoding
      await handleCreateJobsiteAddress(createdJobsite, addressDataDraft, formData, user);

      setHasEdits(false);
      setCreatedJobsiteId(createdJobsite.id);

      // Close confirmation dialog and show success dialog
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);

      // Navigate to jobsite details after a delay
      setTimeout(() => {
        navigate(`/jobsite-management/details/${createdJobsite.id}`);
      }, 2000);

    } catch (error: any) {
      console.error('Error saving address:', error);
      setShowConfirmDialog(false);
      toast.error(error.message || 'Failed to create jobsite');
    } finally {
      setIsProcessing(false);
      setProcessingText('');
    }
  };

  // Navigation between steps
  const handleNavigate = async (direction: 'next' | 'prev') => {
    if (direction === 'prev') {
      setCurrentStep(prev => prev - 1);
      return;
    }

    // Validate and submit current step
    try {
      switch (currentStep) {
        case 1:
          if (!validateStep1()) {
            toast.error('Please fill in all required fields');
            return;
          }
          break;
        case 2:
          if (!validateStep2()) {
            toast.error('Please fill in all required address fields correctly');
            return;
          }
          // Show confirmation dialog instead of submitting directly
          setShowConfirmDialog(true);
          return;
      }

      setCurrentStep(prev => prev + 1);
    } catch (error: any) {
      toast.error(error.message || 'An error occurred. Please try again.');
    }
  };



  // Render current step content
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <JobsiteDetailsStep1
            formData={formData}
            errors={errors}
            tradeOptions={tradeOptions}
            sectors={sectors}
            industries={industries}
            fundingResources={fundingResources}
            contractors={generalContractors}
            selectedContractor={formData.generalContractor}
            completionSteps={completionSteps}
            provinces={provinces}
            onInputChange={handleInputChange}
            onContractorSelect={(value) => {
              setFormData(prev => ({ ...prev, generalContractor: value }));
              if (errors.generalContractor) {
                setErrors(prev => ({ ...prev, generalContractor: '' }));
              }
            }}
            onAddContractor={() => {
              // This is now handled internally by the component
            }}
            onCompletionStepChange={(step) => setFormData(prev => ({
              ...prev,
              completionStep: step,
              completionStatus: step
            }))}
            onActivityStatusChange={(isActive) => setFormData(prev => ({
              ...prev,
              activityStatus: isActive ? 'active' : 'inactive'
            }))}
            onSectorsChange={(values) => {
              setFormData(prev => {
                // Clear industry when sectors change (industries are sector-specific)
                const sectorsChanged = JSON.stringify(values.sort()) !== JSON.stringify(prev.sectors.sort());
                return {
                  ...prev,
                  sectors: values,
                  // Clear industry if sectors changed or multiple sectors selected
                  industry: (values.length > 1 || sectorsChanged) ? '' : prev.industry
                };
              });
              if (errors.sectors) {
                setErrors(prev => ({ ...prev, sectors: '' }));
              }
            }}
            onIndustryChange={(value) => {
              setFormData(prev => ({ ...prev, industry: value }));
              if (errors.industry) {
                setErrors(prev => ({ ...prev, industry: '' }));
              }
            }}
            onFundingResourceChange={(value) => {
              setFormData(prev => ({ ...prev, fundingResource: value }));
              if (errors.fundingResource) {
                setErrors(prev => ({ ...prev, fundingResource: '' }));
              }
            }}
            onMarkAsCompChange={(checked) => {
              setFormData(prev => ({ ...prev, markAsComp: checked }));
            }}
            onProjectStartDateChange={(value) => {
              setFormData(prev => ({ ...prev, projectStartDate: value }));
              if (errors.projectStartDate) {
                setErrors(prev => ({ ...prev, projectStartDate: '' }));
              }
            }}
            onContractorAdded={async (contractorId: string) => {
              // Refresh general contractor list after new contractor is created
              try {
                const updatedContractors = await GeneralContractorService.getGeneralContractors();
                setGeneralContractors(updatedContractors);
                setFormData(prev => ({ ...prev, contractor: contractorId }));
              } catch (error) {
                console.error('Failed to refresh general contractor list:', error);
              }
            }}
          />
        );
      case 2:
        return (
          <AddressStep
            formData={{
              address1: formData.address1,
              address2: formData.address2,
              city: formData.city,
              state: formData.state,
              postalCode: formData.postalCode,
              country: formData.country,
            }}
            errors={errors}
            setFormData={(addressData: any) => {
              setFormData(prev => ({
                ...prev,
                address1: addressData.address1,
                address2: addressData.address2,
                city: addressData.city,
                postalCode: addressData.postalCode,
                state: addressData.state,
                country: addressData.country
              }));
            }}
            setErrors={(addressErrors) => {
              setErrors(prev => ({
                ...prev,
                ...addressErrors
              }));
            }}
            provinces={provinces}
            handleInputChange={handleInputChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50 p-3 sm:p-6">
      <Navigation onBack={handleBack} />

      {/* Main Content */}
      <div className="bg-white rounded-md shadow">
        <div className="px-4 py-5 sm:px-7 sm:py-7 md:px-10 md:py-8 lg:px-14 lg:py-10 xl:px-18">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-semibold">Add New Jobsite</h1>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <StepProgressBar
              currentStep={currentStep}
              steps={FORM_STEPS.map(step => step.title)}
            />
          </div>

          {/* Form Content */}
          <div className="mb-8">
            {renderCurrentStep()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t border-gray-200">
            {currentStep > 1 && (
              <button
                onClick={() => handleNavigate('prev')}
                className="btn-secondary flex items-center gap-2"
                disabled={isProcessing}
                id="previous-step-button"
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={() => handleNavigate('next')}
              className={`btn-primary ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isProcessing}
              id="next-step-button"
            >
              {isProcessing ? processingText : (currentStep === FORM_STEPS.length ? 'Create Jobsite' : 'Next')}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        isOpen={showConfirmDialog}
        onClose={() => !isProcessing && setShowConfirmDialog(false)}
        title={isProcessing ? "Processing" : "Confirm Jobsite Creation"}
      >
        {isProcessing ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#A42823] mb-4"></div>
            <p className="text-gray-600">{processingText || 'Processing...'}</p>
          </div>
        ) : (
          <div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to create this jobsite? Please review all the information before proceeding.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="btn-secondary"
                id="cancel-create-button"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="btn-primary flex items-center gap-2"
                id="confirm-create-button"
              >
                <Plus className="w-4 h-4" />
                Create Jobsite
              </button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        isOpen={showSuccessDialog}
        onClose={() => {}}
        title="Success"
      >
        <div className="text-center py-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-lg font-medium text-gray-900 mb-2">
            Jobsite Created Successfully!
          </p>
          <p className="text-sm text-gray-600">
            Redirecting to jobsite details...
          </p>
        </div>
      </Dialog>

    </div>
  );
};

export default AddJobsite;